import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableContainer,
  TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Stack, Tab, Tabs, MenuItem, Tooltip, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import {
  inventoryApiService,
  InventoryItem,
  InventoryTransaction,
  Product,
  Warehouse,
} from '../../services/inventoryService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PillTone } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDateTime, formatNumber, plural, toNumber } from '../../utils/format';

const NO_WAREHOUSE = 'Brak magazynu dla tego salonu.';

type ApiIssue = { field?: string; message: string };

const unitLabel = (unit?: string) => (unit === 'szt' ? 'szt.' : unit || '');

/** Ilosc bez zbednych zer: 5 → „5”, 0.125 → „0,125”. */
const formatQty = (value: unknown) => {
  const n = Math.round(toNumber(value) * 1000) / 1000;
  const decimals = Number.isInteger(n) ? 0 : (String(n).split('.')[1] || '').length;
  return formatNumber(n, decimals);
};

const stockStatus = (item: InventoryItem): { label: string; tone: PillTone } => {
  const qty = Number(item.quantity);
  const min = Number(item.minQuantity);
  if (qty <= 0) return { label: 'Brak na stanie', tone: 'danger' };
  if (qty <= min && min > 0) return { label: 'Niski stan', tone: 'warning' };
  return { label: 'Dostępny', tone: 'success' };
};

const numCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export const SalonInventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { salonId: paramSalonId } = useParams<{ salonId: string }>();
  const { activeSalonId, loadSalons, setActiveSalonId } = useSalonContextStore();
  const salonId = paramSalonId || activeSalonId || '';
  const [tab, setTab] = useState(0);
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [stock, setStock] = useState<InventoryItem[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<ApiIssue[]>([]);
  const [search, setSearch] = useState('');

  const [purchaseDialog, setPurchaseDialog] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({ productId: '', quantity: '', notes: '' });
  const [adjustDialog, setAdjustDialog] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ productId: '', quantity: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  useEffect(() => {
    if (paramSalonId) {
      setActiveSalonId(paramSalonId);
    }
  }, [paramSalonId, setActiveSalonId]);

  const loadData = useCallback(async () => {
    const sid = salonId;
    if (!sid) { setLoading(false); return; }
    try {
      setLoading(true);
      const whs = await inventoryApiService.getWarehousesForSalon(sid);
      if (whs.length === 0) {
        setError(NO_WAREHOUSE);
        return;
      }
      const wh = whs[0];
      setWarehouse(wh);
      const [stockData, txData, productsData] = await Promise.all([
        inventoryApiService.getWarehouseStock(wh.id),
        inventoryApiService.getTransactions(wh.id, 100),
        inventoryApiService.getProducts({ salonId: sid }),
      ]);
      setStock(stockData);
      setTransactions(txData);
      setProducts(productsData);
    } catch {
      setError('Nie udało się wczytać danych magazynu. Odśwież stronę.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredStock = stock.filter(item =>
    item.product.name.toLowerCase().includes(search.toLowerCase()) ||
    (item.product.sku || '').toLowerCase().includes(search.toLowerCase())
  );

  const lowStockItems = stock.filter(item => Number(item.quantity) <= Number(item.minQuantity) && Number(item.minQuantity) > 0);

  const clearErrors = () => { setError(''); setErrorDetails([]); };

  const readIssues = (e: any): ApiIssue[] => (Array.isArray(e?.response?.data?.details) ? e.response.data.details : []);

  const doPurchase = async () => {
    if (!warehouse) return;
    setSaving(true);
    try {
      await inventoryApiService.purchaseStock(warehouse.id, {
        productId: purchaseForm.productId,
        quantity: parseFloat(purchaseForm.quantity),
        notes: purchaseForm.notes,
      });
      setPurchaseDialog(false);
      clearErrors();
      loadData();
    } catch (e: any) {
      const details = readIssues(e);
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się przyjąć towaru — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się przyjąć towaru. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const doAdjust = async () => {
    if (!warehouse) return;
    setSaving(true);
    try {
      await inventoryApiService.adjustStock(warehouse.id, {
        productId: adjustForm.productId,
        quantity: parseFloat(adjustForm.quantity),
        notes: adjustForm.notes,
      });
      setAdjustDialog(false);
      clearErrors();
      loadData();
    } catch (e: any) {
      const details = readIssues(e);
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się zapisać korekty — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się zapisać korekty. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const openPurchase = () => { clearErrors(); setPurchaseDialog(true); };
  const openAdjust = () => { clearErrors(); setAdjustDialog(true); };

  const txTypeLabel: Record<string, { label: string; tone: PillTone }> = {
    PURCHASE: { label: 'Przyjęcie', tone: 'success' },
    SALE: { label: 'Sprzedaż', tone: 'plum' },
    CONSUMPTION: { label: 'Zużycie w usłudze', tone: 'warning' },
    TRANSFER_IN: { label: 'Przesunięcie – przyjęcie', tone: 'info' },
    TRANSFER_OUT: { label: 'Przesunięcie – wydanie', tone: 'info' },
    ADJUSTMENT: { label: 'Korekta', tone: 'neutral' },
    AUDIT_ADJUST: { label: 'Inwentaryzacja', tone: 'neutral' },
  };

  const FIELD_LABELS: Record<string, string> = { productId: 'Produkt', quantity: 'Ilość', notes: 'Uwagi' };
  const dialogOpen = purchaseDialog || adjustDialog;
  const fieldError = (field: string) => (dialogOpen ? errorDetails.find(d => d.field === field)?.message : undefined);

  const renderError = (inDialog: boolean) => {
    if (!error || (!inDialog && error === NO_WAREHOUSE)) return null;
    const list = inDialog ? errorDetails.filter(d => !d.field || !FIELD_LABELS[d.field]) : errorDetails;
    return (
      <Alert severity="error" onClose={clearErrors} sx={{ mb: 2 }}>
        <Box>{error}</Box>
        {list.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {list.map((item, index) => (
              <li key={`${item.field || 'field'}-${index}`}>
                {item.field && FIELD_LABELS[item.field] ? `${FIELD_LABELS[item.field]}: ` : ''}{item.message}
              </li>
            ))}
          </Box>
        )}
      </Alert>
    );
  };

  const productOptions = products.map(p => (
    <MenuItem key={p.id} value={p.id}>{p.name} ({unitLabel(p.unit)})</MenuItem>
  ));
  const unitOf = (productId: string) => unitLabel(products.find(p => p.id === productId)?.unit);
  const unitAdornment = (productId: string) => {
    const unit = unitOf(productId);
    return unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined;
  };

  // Po zmianie salonu w stanie zostaje jeszcze magazyn poprzedniego — nie pokazujemy go jako biezacego.
  const currentWarehouse = warehouse && (!warehouse.salonId || warehouse.salonId === salonId) ? warehouse : null;
  const noWarehouse = !loading && !currentWarehouse && error === NO_WAREHOUSE;
  const lowCount = lowStockItems.length;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Usługi i magazyn' }]}
        title="Magazyn"
        description={currentWarehouse
          ? `${currentWarehouse.name} · ${countLabel(stock.length, 'pozycja', 'pozycje', 'pozycji')} w magazynie`
          : 'Stany produktów w tym oddziale: przyjęcia, korekty i historia ruchów.'}
        actions={noWarehouse ? undefined : (
          <>
            <Button variant="outlined" startIcon={<TuneIcon />} onClick={openAdjust} disabled={!currentWarehouse}>
              Korekta stanu
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openPurchase} disabled={!currentWarehouse}>
              Przyjmij towar
            </Button>
          </>
        )}
      >
        {currentWarehouse && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
            <Tab label={`Stan magazynu (${stock.length})`} />
            <Tab label="Historia ruchów" />
          </Tabs>
        )}
      </PageHeader>

      {!dialogOpen && renderError(false)}

      {loading && !currentWarehouse ? (
        <LoadingState variant="table" rows={5} />
      ) : noWarehouse ? (
        <SectionCard>
          <EmptyState
            icon={<WarehouseOutlinedIcon />}
            title="Ten salon nie ma jeszcze magazynu"
            description="Magazyn powstaje dla oddziału dodanego do marki. Dodaj oddział w sekcji „Marki i oddziały”, aby prowadzić jego stany."
            actions={<Button variant="outlined" onClick={() => navigate('/salon/brands')}>Przejdź do marek i oddziałów</Button>}
          />
        </SectionCard>
      ) : !currentWarehouse ? null : tab === 0 ? (
        <>
          {lowCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>
                {countLabel(lowCount, 'produkt', 'produkty', 'produktów')} {plural(lowCount, 'ma', 'mają', 'ma')} niski stan:
              </strong>{' '}
              {lowStockItems.map(i => i.product.name).join(', ')}
            </Alert>
          )}

          {stock.length === 0 ? (
            <SectionCard>
              {products.length === 0 ? (
                <EmptyState
                  icon={<Inventory2OutlinedIcon />}
                  title="Najpierw dodaj produkty"
                  description="Magazyn jest pusty, a w katalogu nie ma jeszcze produktów. Dodaj je, a potem przyjmij towar na stan."
                  actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate(`/salon/${salonId}/products`)}>Dodaj produkt</Button>}
                />
              ) : (
                <EmptyState
                  icon={<Inventory2OutlinedIcon />}
                  title="Magazyn jest pusty"
                  description="Przyjmij towar, aby pojawiły się tu stany produktów."
                  actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openPurchase}>Przyjmij towar</Button>}
                />
              )}
            </SectionCard>
          ) : (
            <SectionCard padding="none">
              <Box sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, borderBottom: `1px solid ${tokens.line}` }}>
                <TextField
                  size="small" placeholder="Szukaj po nazwie lub SKU" value={search}
                  onChange={e => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  inputProps={{ 'aria-label': 'Szukaj produktu' }}
                  sx={{ width: { xs: '100%', sm: 320 } }}
                />
              </Box>
              {filteredStock.length === 0 ? (
                <EmptyState
                  compact
                  icon={<SearchIcon />}
                  title="Nic nie znaleziono"
                  description={`Żaden produkt w magazynie nie pasuje do „${search}”.`}
                  actions={<Button variant="text" onClick={() => setSearch('')}>Wyczyść wyszukiwanie</Button>}
                />
              ) : (
                <TableContainer>
                  <Table sx={{ minWidth: 680 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Produkt</TableCell>
                        <TableCell align="right">Stan</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Stan minimalny</TableCell>
                        <TableCell>Kategoria</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredStock.map(item => {
                        const status = stockStatus(item);
                        const unit = unitLabel(item.product.unit);
                        return (
                          <TableRow key={item.id} hover>
                            <TableCell sx={{ minWidth: 200 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>{item.product.name}</Typography>
                              {item.product.sku && (
                                <Typography variant="caption" component="div">SKU {item.product.sku}</Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                ...numCell,
                                fontWeight: 700,
                                color: status.tone === 'danger' ? 'error.main' : status.tone === 'warning' ? tokens.warningText : 'text.primary',
                              }}
                            >
                              {formatQty(item.quantity)} {unit}
                            </TableCell>
                            <TableCell><StatusPill label={status.label} tone={status.tone} /></TableCell>
                            <TableCell align="right" sx={{ ...numCell, color: 'text.secondary' }}>
                              {Number(item.minQuantity) > 0 ? `${formatQty(item.minQuantity)} ${unit}` : '—'}
                            </TableCell>
                            <TableCell sx={{ color: item.product.category ? 'text.primary' : 'text.secondary' }}>
                              {item.product.category || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          )}
        </>
      ) : (
        <SectionCard title="Ostatnie ruchy magazynowe" subtitle="Do 100 najnowszych operacji" padding="none">
          {transactions.length === 0 ? (
            <EmptyState
              compact
              icon={<HistoryOutlinedIcon />}
              title="Brak ruchów magazynowych"
              description="Przyjęcia, sprzedaż, zużycie w usługach i korekty pojawią się tutaj."
            />
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Rodzaj</TableCell>
                    <TableCell>Produkt</TableCell>
                    <TableCell align="right">Ilość</TableCell>
                    <TableCell>Uwagi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map(tx => {
                    const typeInfo = txTypeLabel[tx.type] || { label: tx.type, tone: 'neutral' as PillTone };
                    const qty = Number(tx.quantity);
                    return (
                      <TableRow key={tx.id} hover>
                        <TableCell sx={numCell}>{formatDateTime(tx.createdAt)}</TableCell>
                        <TableCell><StatusPill label={typeInfo.label} tone={typeInfo.tone} /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{tx.product.name}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ ...numCell, fontWeight: 700, color: qty > 0 ? 'success.main' : qty < 0 ? 'error.main' : 'text.secondary' }}
                        >
                          {qty > 0 ? '+' : qty < 0 ? '−' : ''}{formatQty(Math.abs(qty))} {unitLabel(tx.product.unit)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', maxWidth: 240 }}>
                          <Tooltip title={tx.notes || ''}>
                            <Typography noWrap sx={{ fontSize: '0.875rem' }}>{tx.notes || '—'}</Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      )}

      {/* Przyjecie towaru */}
      <Dialog open={purchaseDialog} onClose={() => { setPurchaseDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Przyjmij towar</DialogTitle>
        <DialogContent>
          {purchaseDialog && renderError(true)}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select fullWidth label="Produkt" value={purchaseForm.productId}
              onChange={e => setPurchaseForm(f => ({ ...f, productId: e.target.value }))}
              error={!!fieldError('productId')}
              helperText={fieldError('productId') || (products.length === 0 ? 'Brak produktów — najpierw dodaj je w katalogu.' : undefined)}
            >
              {productOptions}
            </TextField>
            <TextField
              fullWidth label="Ilość" type="number" value={purchaseForm.quantity}
              onChange={e => setPurchaseForm(f => ({ ...f, quantity: e.target.value }))}
              inputProps={{ min: 0, step: 0.001 }}
              InputProps={unitAdornment(purchaseForm.productId)}
              error={!!fieldError('quantity')}
              helperText={fieldError('quantity') || 'Tyle zostanie dodane do obecnego stanu.'}
            />
            <TextField
              fullWidth label="Uwagi (opcjonalnie)" value={purchaseForm.notes} placeholder="np. numer faktury, dostawca"
              onChange={e => setPurchaseForm(f => ({ ...f, notes: e.target.value }))}
              error={!!fieldError('notes')} helperText={fieldError('notes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setPurchaseDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={doPurchase}
            disabled={saving || !purchaseForm.productId || !purchaseForm.quantity}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Przyjmij towar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Korekta stanu */}
      <Dialog open={adjustDialog} onClose={() => { setAdjustDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Skoryguj stan magazynowy</DialogTitle>
        <DialogContent>
          {adjustDialog && renderError(true)}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select fullWidth label="Produkt" value={adjustForm.productId}
              onChange={e => setAdjustForm(f => ({ ...f, productId: e.target.value }))}
              error={!!fieldError('productId')} helperText={fieldError('productId')}
            >
              {productOptions}
            </TextField>
            <TextField
              fullWidth label="Zmiana ilości" type="number" value={adjustForm.quantity} placeholder="np. 5 lub -3"
              onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))}
              inputProps={{ step: 0.001 }}
              InputProps={unitAdornment(adjustForm.productId)}
              error={!!fieldError('quantity')}
              helperText={fieldError('quantity') || 'Wartość dodatnia zwiększa stan, ujemna go zmniejsza.'}
            />
            <TextField
              fullWidth label="Powód korekty" value={adjustForm.notes} placeholder="np. uszkodzone opakowanie"
              onChange={e => setAdjustForm(f => ({ ...f, notes: e.target.value }))}
              error={!!fieldError('notes')} helperText={fieldError('notes')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setAdjustDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={doAdjust}
            disabled={saving || !adjustForm.productId || !adjustForm.quantity}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Zastosuj korektę
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
